import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { IconStarFilled } from '@tabler/icons-react';
import {
  Badge,
  Button,
  Card,
  Group,
  Image,
  Modal,
  Stack,
  Text,
} from '@mantine/core';
import { ProductItem } from '@/src/models/produk-kesehatan';
import { formatCurrency, toTitleCase } from '@/src/shared/utils/common';

interface ProductCardProps {
  product: ProductItem;
}

export function ProductCard({ product }: ProductCardProps) {
  const router = useRouter();
  const [opened, setOpened] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const formattedPrice = product.price_range || '0';
  const finalFormattedPrice = product.final_price_range || '0';
  const isSamePrice = formattedPrice === finalFormattedPrice;
  const href = `/detail-item/product/${product.id}`;

  const handleClick = () => {
    if (product.is_ethical) {
      setOpened(true);
      return;
    }

    if (isNavigating) {
      return;
    }

    setIsNavigating(true);
    router.push(href);
  };

  const handleConfirm = () => {
    setOpened(false);
    router.push('/consultation');
  };

  return (
    <>
      <Card
        padding="0"
        radius="md"
        mt="md"
        onClick={handleClick}
        className={isNavigating ? 'cursor-progress' : 'cursor-pointer'}
        h={{ base: '27rem', md: '26rem' }}
        withBorder
      >
        <Image
          src={product.is_ethical ? '/images/logo.png' : product.image}
          alt={product.product_name}
          mih={{ base: 256, sm: 220 }}
          h="100%"
          fit="contain"
          fallbackSrc="/images/logo.png"
          className="rounded-t-md"
        />

        <Stack
          gap="0.1rem"
          my="sm"
          px={{ base: 'sm', md: 'md' }}
          h="60%"
          justify="space-between"
        >
          <Stack gap={4} align="flex-start">
            {product.is_ethical && (
              <Badge color="red" variant="light" size="sm">
                Produk Resep
              </Badge>
            )}

            <Stack gap={0}>
              <Text lineClamp={2} fz="sm" fw={500}>
                {toTitleCase(product.product_name)}
              </Text>

              {!product.is_ethical && (
                <>
                  <Text fw="bold" fz="sm" mt={2}>
                    {formatCurrency(finalFormattedPrice)}
                  </Text>

                  {!isSamePrice && (
                    <Text
                      c="dimmed"
                      fz={{ base: 'xs', md: '0.75rem' }}
                      td="line-through"
                    >
                      {formatCurrency(formattedPrice)}
                    </Text>
                  )}
                </>
              )}
            </Stack>
          </Stack>

          <Stack gap={0}>
            <Group gap={6}>
              <IconStarFilled size={15} color="gold" />
              <Text c="dimmed" size="xs">
                {product.star_rating} | {product.transaction_count} terjual
              </Text>
            </Group>
            <Text size="xs" lineClamp={1} c="dimmed">
              {product.branch_name}
            </Text>
            <Text size="xs" c="dimmed">
              ± {product.distance_string || '0'}
            </Text>
          </Stack>
        </Stack>
      </Card>
      <Modal
        opened={opened}
        onClose={() => setOpened(false)}
        title="Konfirmasi"
        centered
      >
        <Text size="sm" mb="md">
          Produk ini memerlukan resep dokter. Lanjut ke konsultasi?
        </Text>

        <Button fullWidth onClick={handleConfirm}>
          Ya, lanjut
        </Button>
      </Modal>
    </>
  );
}
